import { CachedImageProvider } from '@bestseller/retail-planning.styletimeline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Box, Stack, styled, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import {
    ColDef,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    ProcessCellForExportParams,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FullScreenContainer } from 'src/components/atoms/FullScreenContainer/FullScreenContainer';
import { CountStatusBar } from 'src/components/molecules/CountStatusBar/CountStatusBar';
import EditAllPartnersButton from 'src/components/molecules/EditAllPartnersButton/EditAllPartnersButton';
import { CustomNoRowsOverlay } from 'src/components/molecules/NoRowsOverlay/NoRowsOverlay';
import { StorefrontTypeSelect } from 'src/components/molecules/StorefrontTypeSelect/StorefrontTypeSelect';
import { ActionButtons } from 'src/components/organisms/ActionButtons/ActionsButtons';
import EnableAdvancedFiltersButton from 'src/components/organisms/ActionButtons/EnableAdvancedFiltersButton';
import SettingsDropdown from 'src/components/organisms/ActionButtons/SettingsDropdown';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { DataTableLoading } from 'src/components/organisms/DataTable/DataTableLoading';
import { defaultRowGroupOptions } from 'src/components/organisms/DataTable/options/row-group-options';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { Module, ScopeEnum } from 'src/domain';
import {
    StyleTimelineRowUpdatedEvent,
    StyleTimelineRowUpdateEventPayload,
} from 'src/domain/events/styletimeline-row-updated.event';
import { StyleTimelineOverviewRow } from 'src/domain/table/style-timeline-overview-row';
import { useScope, useUnsavedChangesModule } from 'src/hooks';
import { useChainFurnitureZoneQuery } from 'src/hooks/chain-furniture-zone/queries/useChainFurnitureZoneQuery';
import { useDiscardChangesModule } from 'src/hooks/discard-changes/useDiscardChangesModule';
import { useInventoryMasterDataQuery } from 'src/hooks/inventory-master-data/queries/useInventoryMasterDataQuery';
import { useDiscardShortcut } from 'src/hooks/shortcuts/useDiscardShortcut';
import { useSaveShortcut } from 'src/hooks/shortcuts/useSaveShortcut';
import { useStores } from 'src/hooks/store-selection/queries/useStores';
import { useStyleCategoriesQuery } from 'src/hooks/style-categories/queries/useStyleCategoriesQuery';
import { useStyleTimelineSaveChanges } from 'src/hooks/style-timeline/save-changes/useStyleTimelineSaveChanges';
import { useStyleTimelineSimulationRows } from 'src/hooks/style-timeline/simulation/useStyleTimelineSimulationRows';
import { usePhaseoutProfiles } from 'src/hooks/style-timeline/usePhaseoutProfiles';
import { useReplenishmentPhaseoutMethods } from 'src/hooks/style-timeline/useReplenishmentPhaseoutMethods';
import { useStyleTimelineHandleCellValueChanges } from 'src/hooks/style-timeline/useStyleTimelineHandleCellValueChanges';
import { useIsDevEnv } from 'src/hooks/useIsDevEnv';
import { useProductLifecycleTypes } from 'src/hooks/useProductLifecycleTypes';
import { collectionsToMap, mapModelToRow, salesCampaignsToMap } from 'src/mapping/style-timeline.mapping';
import { useContextMenuItems } from 'src/utils/ag-grid/getContextMenuItems';
import { formatAndSendToClipboard, generateSelectOptions } from 'src/utils/ag-grid/styleTimelineHelper';
import { formatMonthYearAsHumanReadable } from 'src/utils/formatAsMonthYear';
import { formatWeekYearAsHumanReadable } from 'src/utils/formatAsWeekYear';
import { columns, defaultColDef, globalColumns, lifecycleValueGetter } from './data/columns';

const processStyleTimelineCellForExport = (params: ProcessCellForExportParams): string => {
    const value = params.value;

    switch (params.column.getColId()) {
        case 'startWeek':
        case 'normClosureWeek':
        case 'normClosureWeekExpected':
            return value ? formatWeekYearAsHumanReadable(Number(value)) : '';
        case 'nextKnownDeliveryDate':
            return value ? formatMonthYearAsHumanReadable(Number(value)) : '';
        default:
            return params.formatValue(value);
    }
};

interface StyleTimelineProps {
    disableGlobalPlanning?: boolean;
    disableStorefrontTypeSelect?: boolean;
    disableActionButtons?: boolean;
    storefrontType?: string;
    header?: React.ReactNode;
}

export const StyleTimeline: FC<StyleTimelineProps> = ({
    header,
    disableGlobalPlanning = false,
    disableStorefrontTypeSelect = false,
    disableActionButtons = false,
    storefrontType,
}) => {
    const { data: stores } = useStores();
    const {
        data: overviewRows,
        loading: rowsLoading,
        error,
        collections,
        salesCampaigns,
        updateCache,
    } = useStyleTimelineSimulationRows(storefrontType);

    const { data: productLifecycleTypes } = useProductLifecycleTypes();

    const { data: styleCategories } = useStyleCategoriesQuery();
    const [fullscreen, setFullscreen] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [enableAdvancedFiltering, setEnableAdvancedFiltering] = useState(false);

    const scope = useScope();
    const isGlobalPlanning = useMemo(() => scope === ScopeEnum.GLOBAL, [scope]);

    const handleCellValueChanges = useStyleTimelineHandleCellValueChanges(overviewRows);

    const { data: phaseoutProfiles, loading: phaseoutProfilesLoading } = usePhaseoutProfiles();
    const { data: replenishmentPhaseoutMethods } = useReplenishmentPhaseoutMethods();
    const { data: chainFurnitures } = useInventoryMasterDataQuery();
    const { data: chainFurnitureZones } = useChainFurnitureZoneQuery();

    const isDevEnv = useIsDevEnv();

    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            error: !!error,
            noRowsMessageFunc: () => (error ? error.message : 'No Rows To Show.'),
        };
    }, [error]);

    useEffect(() => {
        if (isDevEnv) {
            // biome-ignore lint/suspicious/noExplicitAny: we need to use any
            (window as any)['__StyleTimelineApi__'] = {
                setColumnsVisible: (columns: string[], visible: boolean) => {
                    if (gridApi) {
                        gridApi.setColumnsVisible(columns, visible);
                        return true;
                    }
                    return false;
                },
                ensureColumnVisible: (column: string) => {
                    if (gridApi) {
                        gridApi.ensureColumnVisible(column);
                        return true;
                    }
                    return false;
                },
            };
        } else {
            // biome-ignore lint/suspicious/noExplicitAny: we need to use any
            (window as any)['__StyleTimelineApi__'] = undefined;
        }
    }, [isDevEnv, gridApi]);

    const rowGroupOptions = useMemo(() => {
        return {
            ...defaultRowGroupOptions,
            groupDefaultExpanded: isGlobalPlanning ? 1 : 2,
            groupHideParentOfSingleChild: false,
            getRowHeight: isGlobalPlanning ? undefined : defaultRowGroupOptions.getRowHeight,
        };
    }, [isGlobalPlanning]);

    useEffect(() => {
        // Note: For performance reasons, we're not using cache for styletimeline global planning, thus we need to manually update the grid with the data from the server.
        const handleRowUpdate = (event: Event) => {
            const { rows } = (event as CustomEvent<StyleTimelineRowUpdateEventPayload>).detail;
            if (rows) {
                const collectionMap = collectionsToMap(collections);
                const salesCampaignMap = salesCampaignsToMap(salesCampaigns);
                const mappedRows = rows.map((row) =>
                    mapModelToRow(row, collectionMap, salesCampaignMap, stores?.allStores ?? [], styleCategories)
                );
                updateCache(mappedRows);
            }
        };

        globalThis.addEventListener(StyleTimelineRowUpdatedEvent, handleRowUpdate);
        return () => {
            globalThis.removeEventListener(StyleTimelineRowUpdatedEvent, handleRowUpdate);
        };
    }, [collections, salesCampaigns, styleCategories, updateCache, stores?.allStores]);

    const handleGridReady = useCallback((event: GridReadyEvent) => {
        setGridApi(event.api);
    }, []);

    const doesExternalFilterPass = useCallback((node: IRowNode<StyleTimelineOverviewRow>): boolean => {
        return !node.data?.exitWeek && node.data?.exitWeekType !== 'INFINITE';
    }, []);

    const [saveChanges, { isSaving }] = useStyleTimelineSaveChanges(storefrontType);
    const [unsavedChanges] = useUnsavedChangesModule<StyleTimelineOverviewRow[]>(Module.StyleTimeline);
    const hasUnsavedChanges = !!unsavedChanges && unsavedChanges.length > 0;
    const discardChanges = useDiscardChangesModule();

    const tableColumns = useMemo(() => {
        const cols = isGlobalPlanning ? [...globalColumns] : [...columns];
        if (scope !== ScopeEnum.PARTNER) {
            const activeSalesCampaignColumnIndex = cols.findIndex((col) => col.field === 'activeSalesCampaigns');
            if (activeSalesCampaignColumnIndex >= 0) {
                cols.splice(activeSalesCampaignColumnIndex, 1);
            }
        }

        const phaseoutProfileColumn = cols.find((col) => col.field === 'phaseoutProfileId');
        const productLifecycleColumn = cols.find((col) => col.field === 'productLifeCycleIds');
        const chainFurnitureColumn = cols.find((col) => col.field === 'chainFurnitureId');
        const chainFurnitureZoneColumn = cols.find((col) => col.field === 'chainFurnitureZoneId');
        if (phaseoutProfileColumn) {
            phaseoutProfileColumn.cellEditorParams = {
                selectOptions: generateSelectOptions(phaseoutProfiles, 'No phaseout profiles available'),
            };
        }

        const replenishmentPhaseoutMethodColumn = cols.find((col) => col.field === 'replenishmentPhaseoutMethodId');
        if (replenishmentPhaseoutMethodColumn) {
            replenishmentPhaseoutMethodColumn.cellEditorParams = {
                selectOptions: generateSelectOptions(
                    replenishmentPhaseoutMethods,
                    'No replenishment phaseout methods available'
                ),
            };
        }

        if (chainFurnitureColumn) {
            chainFurnitureColumn.cellEditorParams = {
                selectOptions: generateSelectOptions(chainFurnitures, 'No furnitures available'),
            };
        }

        if (chainFurnitureZoneColumn) {
            chainFurnitureZoneColumn.cellEditorParams = {
                selectOptions: generateSelectOptions(chainFurnitureZones, 'No furniture zones available'),
            };
        }
        if (productLifecycleColumn) {
            productLifecycleColumn.valueGetter = (params: ValueGetterParams) =>
                lifecycleValueGetter(params, productLifecycleTypes);
            productLifecycleColumn.filterValueGetter = (params: ValueGetterParams) =>
                lifecycleValueGetter(params, productLifecycleTypes);
        }

        return [...cols].sort((a, b) => {
            const aLabel = String(a.headerName ?? a.field ?? '');
            const bLabel = String(b.headerName ?? b.field ?? '');
            return aLabel.localeCompare(bLabel);
        });
    }, [
        isGlobalPlanning,
        scope,
        phaseoutProfiles,
        replenishmentPhaseoutMethods,
        productLifecycleTypes,
        chainFurnitures,
        chainFurnitureZones,
    ]);

    const processCellForClipboard = useCallback((params: ProcessCellForExportParams) => {
        /**
         * Value is either primitive or a tuple (used in Gross Margin)
         * @see {@link GrossMargin}
         */
        const value = params.value as unknown;

        let parsedValue: unknown;

        // assume predicted value is second, if not present default to current value
        if (Array.isArray(value)) {
            parsedValue = value[1] ?? value[0];
        } else {
            parsedValue = value;
        }

        const colDef: ColDef = params.column.getColDef();
        if (typeof colDef.valueFormatter === 'function') {
            return colDef.valueFormatter({
                ...params,
                data: params.node?.data,
                colDef: colDef,
            } as ValueFormatterParams);
        }

        return JSON.stringify(parsedValue ?? '');
    }, []);

    const clearSettings = () => {
        if (gridApi) {
            gridApi.setFilterModel(null);
            gridApi.setAdvancedFilterModel(null);
            gridApi.resetColumnState();
        }
    };

    const getContextMenuItems = useContextMenuItems(Module.StyleTimeline);

    const isGridReady = !rowsLoading;
    const loading = rowsLoading || isSaving || phaseoutProfilesLoading;

    useEffect(() => {
        if (!isGridReady) setGridApi(null);
    }, [isGridReady]);

    useSaveShortcut(saveChanges, !hasUnsavedChanges);
    useDiscardShortcut(discardChanges, !hasUnsavedChanges);

    return (
        <>
            {!header && (
                <Stack
                    sx={{
                        mb: 2,
                    }}
                >
                    <SmartModuleTitleCard />
                </Stack>
            )}
            <FullScreenContainer fullscreen={fullscreen}>
                <Stack
                    sx={{
                        height: '100%',
                    }}
                >
                    <Stack
                        className="table-toolbar"
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            marginBottom: 6,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            gap: 2,

                            '@media (min-width: 1550px)': {
                                justifyContent: 'space-between',
                            },
                        }}
                    >
                        {header && (
                            <Stack
                                sx={{
                                    marginRight: 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                {header}
                            </Stack>
                        )}
                        {scope !== ScopeEnum.STORE && !disableStorefrontTypeSelect && <StorefrontTypeSelect />}
                        <Box
                            sx={{
                                ml: 'auto',
                            }}
                        >
                            <ActionButtons
                                showDiscardButton={!disableActionButtons}
                                showSaveButton={!disableActionButtons}
                                discardDisabled={!hasUnsavedChanges}
                                loading={loading}
                                saveLoading={isSaving}
                                saveDisabled={!hasUnsavedChanges}
                                onDiscard={discardChanges}
                                onSave={saveChanges}
                                hideAuditLogButton
                            >
                                <SettingsDropdown
                                    gridApi={gridApi}
                                    module={Module.StyleTimeline}
                                    setEnableAdvancedFiltering={setEnableAdvancedFiltering}
                                />
                                <ToggleButtonGroup>
                                    <Tooltip
                                        title={fullscreen ? 'Close fullscreen' : 'Open fullscreen'}
                                        className="fullscreen-button"
                                    >
                                        <ToggleButton
                                            value="fullscreen"
                                            selected={fullscreen}
                                            color="primary"
                                            onClick={() => setFullscreen((prev) => !prev)}
                                        >
                                            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                        </ToggleButton>
                                    </Tooltip>
                                    <EnableAdvancedFiltersButton
                                        gridApi={gridApi}
                                        clearSettings={clearSettings}
                                        enableAdvancedFiltering={enableAdvancedFiltering}
                                        setEnableAdvancedFiltering={setEnableAdvancedFiltering}
                                    />
                                    {!disableGlobalPlanning && <EditAllPartnersButton />}
                                </ToggleButtonGroup>
                            </ActionButtons>
                        </Box>
                    </Stack>

                    <StyledTableWrapper
                        data-testid="style-timeline-table-wrapper"
                        className="ag-theme-alpine-custom-compact small-font"
                        sx={{ flex: 1 }}
                        hasUnsavedChanges={hasUnsavedChanges}
                    >
                        <CachedImageProvider>
                            {!isGridReady ? (
                                <DataTableLoading />
                            ) : (
                                <DataTable
                                    className="my-first-step"
                                    {...rowGroupOptions}
                                    includeHiddenColumnsInAdvancedFilter={true}
                                    sendToClipboard={formatAndSendToClipboard}
                                    processCellForClipboard={processCellForClipboard}
                                    defaultCsvExportParams={{
                                        processCellCallback: processStyleTimelineCellForExport,
                                    }}
                                    defaultExcelExportParams={{
                                        processCellCallback: processStyleTimelineCellForExport,
                                    }}
                                    rows={overviewRows}
                                    columns={tableColumns}
                                    defaultColDef={defaultColDef}
                                    domLayout="normal"
                                    onGridReady={handleGridReady}
                                    groupDisplayType="groupRows"
                                    doesExternalFilterPass={doesExternalFilterPass}
                                    groupRowRendererParams={{
                                        innerRenderer: GroupRowInnerRenderer,
                                        suppressDoubleClickExpand: true,
                                        suppressEnterExpand: true,
                                    }}
                                    isNumberTable={false}
                                    animateRows={true}
                                    getContextMenuItems={getContextMenuItems}
                                    onCellValuesChanged={handleCellValueChanges}
                                    loading={isSaving}
                                    skipForceRefresh
                                    gridOptions={{
                                        statusBar: {
                                            statusPanels: [
                                                {
                                                    statusPanel: CountStatusBar,
                                                    align: 'right',
                                                },
                                            ],
                                        },
                                        enableAdvancedFilter: enableAdvancedFiltering,
                                    }}
                                    sideBar={{
                                        position: 'left',
                                        toolPanels: [
                                            {
                                                id: 'filters',
                                                labelDefault: 'Filters',
                                                labelKey: 'filters',
                                                iconKey: 'filter',
                                                toolPanel: 'agFiltersToolPanel',

                                                toolPanelParams: {
                                                    suppressExpandAll: false,
                                                    className: 'ag-filter-toolpanel-button',
                                                },
                                            },
                                            {
                                                id: 'columns',
                                                labelDefault: 'Columns',
                                                labelKey: 'columns',
                                                iconKey: 'columns',
                                                toolPanel: 'agColumnsToolPanel',
                                                toolPanelParams: {
                                                    suppressExpandAll: false,
                                                },
                                            },
                                        ],
                                    }}
                                    noRowsOverlayComponent={CustomNoRowsOverlay}
                                    noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                                />
                            )}
                        </CachedImageProvider>
                    </StyledTableWrapper>
                </Stack>
            </FullScreenContainer>
        </>
    );
};

const StyledTableWrapper = styled(TableWrapper)`
  .ag-root-wrapper {
    background-color: #fafafa;
  }

  .ag-row {
    background-color: #fff;
  }

  .ag-pivot-mode-panel,
  .ag-last-column-drop {
    display: none;
  }
`;

export const GroupRowInnerRenderer: FC<ICellRendererParams> = ({ node }) => {
    return (
        <Box
            sx={{
                width: '100%',
                fontWeight: 'bold',
            }}
        >
            {node.key}
        </Box>
    );
};
